import {
  Badge,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowsOutSimple,
  CheckSquare,
  Paperclip,
  X,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { getAttachmentIcon, getFileExtension } from '../../constants';
import { ViewAttachmentModal } from '../../report-attachments/Attachment';
import { useProcessForm } from '../ProcessesTab';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FilePicker, FilePickerDropzone } from '~/components/ui/file-picker';
import { useFormController } from '~/components/ui/form';
import { Tab, TabList, TabPanel, Tabs } from '~/components/ui/tabs';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { FileUploadService } from '~/services/api/file-upload/endpoint';
import { ProcessService } from '~/services/api/processes/endpoints';
import { AttachmentItem } from '~/services/api/processes/types';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { isMutationSuccess, useWrappedMutation } from '~/services/api/rest-api';
import { UserService } from '~/services/api/users/endpoints';

type AttachmentType = {
  file_id: number;
  file_name: string;
  is_uploaded: boolean;
};

export function AttachFileButton({
  type = 'process',
  conversationId,
}: Readonly<{
  type: 'process' | 'message';
  conversationId?: string;
}>) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const referenceId = z.coerce.string().catch('').parse(useParams().reportId);
  const mainForm = useProcessForm();
  const workflowId = mainForm?.watch('workflowId');
  const controller = useFormController();
  const files = Array.isArray(controller?.field.value)
    ? (controller?.field.value as AttachmentType[])
    : ([] as AttachmentType[]);

  const attachmentsQuery =
    ProcessService.endpoints.listAllReportAttachments.useQuery(
      {
        workflow_id: workflowId ?? -1,
        params: {
          reference_id: referenceId,
        },
      },
      { skip: !workflowId },
    );

  const setIdsForAttachments = (id: number, name: string) => {
    // adds file in array if not in array and remove it if it exists
    let updatedFiles = [...files];
    const fileUpload = updatedFiles.find((file) => file.file_id === id);
    if (fileUpload) {
      updatedFiles = updatedFiles.filter((file) => file.file_id !== id);
    } else {
      updatedFiles.push({
        file_id: id,
        file_name: name,
        is_uploaded: false,
      });
    }
    controller?.field.onChange(updatedFiles);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} isDismissable={false}>
      <DialogTrigger asChild>
        <Tooltip title={intl.formatMessage({ defaultMessage: 'Attach File' })}>
          <IconButton>
            <Badge badgeContent={files?.length}>
              <Paperclip />
            </Badge>
          </IconButton>
        </Tooltip>
      </DialogTrigger>
      <DialogContent size='3xl' className='flex flex-col gap-4'>
        <div className='flex justify-between'>
          <DialogTitle>
            <FormattedMessage defaultMessage='Attach File' />
          </DialogTitle>
          <DialogClose>
            <IconButton
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <X />
            </IconButton>
          </DialogClose>
        </div>
        <Tabs defaultValue='upload-file'>
          <TabList>
            <Tab value='upload-file'>
              <Typography>
                <FormattedMessage defaultMessage='Upload Files' />
              </Typography>
            </Tab>
            {/* Only show the select file tab for process type */}
            {type === 'process' && (
              <Tab value='select-file'>
                <Typography>
                  <FormattedMessage defaultMessage='Choose From Files' />
                </Typography>
              </Tab>
            )}
          </TabList>
          <TabPanel value='upload-file'>
            <UploadFiles type={type} conversationId={conversationId} />
          </TabPanel>
          <TabPanel value='select-file'>
            <QueryResolver query={attachmentsQuery}>
              {(data) => (
                <div className='flex flex-col gap-4 max-h-[400px] overflow-y-auto'>
                  <div>
                    <Typography>
                      <FormattedMessage defaultMessage='Choose from files uploaded on this report' />
                    </Typography>
                  </div>
                  <div className='grid grid-cols-6 gap-2'>
                    {data.map((attachment) => (
                      <button
                        className='flex self-stretch'
                        key={attachment.id}
                        onClick={() =>
                          setIdsForAttachments(
                            attachment.id,
                            attachment.original_file_name,
                          )
                        }
                        aria-label='add attachment'
                      >
                        <ReportAttachmentItem
                          added={
                            !!files?.find(
                              (file) => file.file_id === attachment.id,
                            )
                          }
                          {...attachment}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </QueryResolver>
          </TabPanel>
        </Tabs>
        <Button onClick={() => setIsOpen(false)}>
          <FormattedMessage defaultMessage='Attach' />
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function UploadFiles({
  type,
  conversationId,
}: Readonly<{ type: 'process' | 'message'; conversationId?: string }>) {
  const controller = useFormController();
  const files = Array.isArray(controller?.field.value)
    ? (controller?.field.value as AttachmentType[])
    : ([] as AttachmentType[]);

  const [uploadProcessFile, uploadProcessFileState] =
    FileUploadService.endpoints.uploadFileForProcessComment.useMutation();

  const [uploadMessageFile, uploadMessageFileState] =
    FileUploadService.endpoints.uploadMessageAttachmentProcess.useMutation();

  const processFiles = async (uploadedFiles: File[]) => {
    const promiseAll = uploadedFiles.map(async (file) => {
      // If the type is message and the conversationId is provided, upload the file to the message
      if (type === 'message' && conversationId) {
        const upload = await uploadMessageFile({
          file: file,
          conversationId: conversationId,
          startUploadData: {
            file_name: file.name,
            file_type: getFileExtension(file),
            file_size: file.size,
          },
        });
        if (isMutationSuccess(upload)) {
          return {
            file_id: upload.data.id,
            file_name: file.name,
            is_uploaded: true,
          };
        }
      } else {
        // If the type is process, upload the file to the process
        const upload = await uploadProcessFile({
          file: file,
          startUploadData: {
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
          },
        });
        if (isMutationSuccess(upload)) {
          return {
            file_id: upload.data.id,
            file_name: file.name,
            is_uploaded: true,
          };
        }
      }
    });
    const results = await Promise.all(promiseAll);
    controller?.field.onChange([...files, ...results]);
  };

  const removeFile = (fileId: number) => {
    controller?.field.onChange(files?.filter((f) => f.file_id !== fileId));
  };

  return (
    <div>
      <FilePicker
        maxFiles={Infinity}
        onFileAccept={(details) => processFiles(details.files)}
      >
        <FilePickerDropzone />
        {uploadMessageFileState.isLoading ||
        uploadProcessFileState.isLoading ? (
          <LinearProgress />
        ) : (
          <div>
            {files
              ?.filter((file) => file.is_uploaded)
              .map((file) => (
                <FileForUpload
                  key={file.file_id}
                  item={{
                    id: file?.file_id,
                    name: file?.file_name,
                  }}
                  removeFile={removeFile}
                />
              ))}
          </div>
        )}
      </FilePicker>
    </div>
  );
}

function FileForUpload({
  item,
  removeFile,
}: Readonly<{
  item: {
    id: number;
    name: string;
  };
  removeFile: (fileId: number) => void;
}>) {
  return (
    <div>
      <div className='grid gap-2 p-2'>
        <div className='flex items-center gap-2'>
          <div className='max-w-64 w-full flex flex-col space-y-0.5'>
            <Typography level='body-sm' className='truncate'>
              {item.name}
            </Typography>
          </div>
          <div className='ml-auto'>
            <Button
              size='sm'
              variant='plain'
              color='danger'
              onClick={() => {
                removeFile(item.id);
              }}
            >
              <FormattedMessage defaultMessage='Remove File' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReportAttachmentItem({
  added,
  ...item
}: AttachmentItem & { added?: boolean }) {
  const intl = useIntl();
  const slug = useTenantSlug();
  const referenceId = z.coerce.string().catch('').parse(useParams().reportId);
  const { createToastNotification } = useToastNotificationContext();
  const [isHovered, setIsHovered] = useState(false);
  const flagsQuery = UserService.endpoints.flags.useQuery();
  const flags = flagsQuery.data;

  const downloadReportFile = useWrappedMutation(
    ReportService.endpoints.downloadReportFile.useMutation(),
  );

  const onHover = () => {
    setIsHovered(true);
  };

  const exitHover = () => {
    setIsHovered(false);
  };

  //DOWNLOAD FILE

  const downloadAttachment = async () => {
    if (flags?.is_allowed_to_download_attachments) {
      downloadReportFile.mutateAsync(
        {
          file: item,
          report_reference: referenceId,
          slug: slug,
        },
        {
          onSuccess: (data) => {
            const downloadUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', item.original_file_name);
            document.body.appendChild(link);
            link.click();
          },
        },
      );
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'You are not allowed to download attachments',
        }),
        'error',
      );
    }
  };

  return (
    <Card
      className='relative w-24 h-24 border-2 !p-1'
      onMouseOver={onHover}
      onMouseOut={exitHover}
    >
      <div className='absolute top-1 left-1'>
        {added && (
          <CheckSquare
            weight='fill'
            className='text-brand-primary-600 bg-white'
          />
        )}
      </div>
      {isHovered && (
        <div className='absolute top-1 right-1'>
          <ViewAttachmentModal
            file={item}
            downloadAttachment={downloadAttachment}
          >
            <IconButton variant='solid' size='sm' color='secondary'>
              <ArrowsOutSimple weight='fill' />
            </IconButton>
          </ViewAttachmentModal>
        </div>
      )}
      <img
        src={getAttachmentIcon(item.file_type, item.url)}
        alt={item.original_file_name}
        className='w-24'
      />
    </Card>
  );
}
